import {
  CreatePricingOfferInput,
  PricingOffer,
  PricingOfferAdminFilter,
  UpdatePricingOfferInput
} from "@outschool/gql-backend-generated";
import {
  ExecutionResult,
  MutationResult,
  gql,
  useMutation,
  useQueryWithPreviousData
} from "@outschool/ui-apollo";
import React from "react";

import {
  AdminPricingOfferQuery,
  AdminPricingOfferQueryVariables,
  AdminPricingOffersQuery,
  AdminPricingOffersQueryVariables,
  CreatePricingOfferMutation,
  CreatePricingOfferMutationVariables,
  DeletePricingOfferMutation,
  DeletePricingOfferMutationVariables,
  EditPricingOfferMutation,
  EditPricingOfferMutationVariables
} from "../../shared/generated/graphql";

const AdminPricingOffers = gql`
  query AdminPricingOffers($filter: PricingOfferAdminFilter!) {
    pricingOffers(filter: $filter) {
      uid
      name
      buyerOrgName
      deletedAt
    }
  }
`;

const PricingOfferAdminFragment = gql`
  fragment PricingOfferAdminFragment on PricingOffer {
    uid
    name
    buyerOrgName
    totalCapCents
    userLimitCents
    startAfter
    endBy
    requirePaymentMethod
    discount
    isFinancialAid
    eligibleEmailDomains
    deletedAt
    eligibilityLastUpdatedAt
    includedEmailAddresses
    excludedEmailAddresses
    organizationUid
  }
`;

export function useAdminPricingOffers(showDeleted: boolean) {
  const {
    error,
    loading,
    data: { pricingOffers = [] } = {}
  } = useQueryWithPreviousData<
    AdminPricingOffersQuery,
    AdminPricingOffersQueryVariables
  >(AdminPricingOffers, {
    /* Use network only to avoid potential cache inconsistency issues */
    fetchPolicy: "network-only",
    variables: {
      filter: showDeleted
        ? PricingOfferAdminFilter.Deleted
        : PricingOfferAdminFilter.Active
    }
  });
  return { pricingOffers, error, loading };
}

export const CreatePricingOffer = gql`
  mutation CreatePricingOffer($data: CreatePricingOfferInput) {
    createPricingOffer(data: $data) {
      ...PricingOfferAdminFragment
    }
  }
  ${PricingOfferAdminFragment}
`;

export function useCreatePricingOffer(): [
  (
    data: CreatePricingOfferInput
  ) => Promise<ExecutionResult<CreatePricingOfferMutation>>,
  MutationResult<CreatePricingOfferMutation>
] {
  const [mutate, result] = useMutation<
    CreatePricingOfferMutation,
    CreatePricingOfferMutationVariables
  >(CreatePricingOffer, {
    update: (cache, result: ExecutionResult<CreatePricingOfferMutation>) => {
      const data = cache.readQuery<
        AdminPricingOffersQuery,
        AdminPricingOffersQueryVariables
      >({ query: AdminPricingOffers });
      const pricingOffers = (data?.pricingOffers ?? []).concat(
        // @ts-ignore TS(2769): No overload matches this call.
        result.data?.createPricingOffer
      );
      cache.writeQuery<
        AdminPricingOffersQuery,
        AdminPricingOffersQueryVariables
      >({
        query: AdminPricingOffers,
        data: {
          pricingOffers
        }
      });
    }
  });
  const wrapped = React.useCallback(
    async (data: CreatePricingOfferInput) => {
      return await mutate({ variables: { data } });
    },
    [mutate]
  );
  return [wrapped, result];
}

const AdminPricingOffer = gql`
  query AdminPricingOffer($uid: String!) {
    pricingOffer(uid: $uid) {
      ...PricingOfferAdminFragment
    }
  }
  ${PricingOfferAdminFragment}
`;

export function useAdminPricingOffer(uid: string) {
  const {
    error,
    loading,
    data: { pricingOffer } = {}
  } = useQueryWithPreviousData<
    AdminPricingOfferQuery,
    AdminPricingOfferQueryVariables
  >(AdminPricingOffer, {
    variables: { uid },
    /* Use network only to avoid potential cache inconsistency issues */
    fetchPolicy: "network-only"
  });
  return { pricingOffer, error, loading };
}

export const EditPricingOffer = gql`
  mutation EditPricingOffer($uid: String!, $data: UpdatePricingOfferInput) {
    updatePricingOffer(uid: $uid, data: $data) {
      ...PricingOfferAdminFragment
    }
  }
  ${PricingOfferAdminFragment}
`;

export function useEditPricingOffer(
  uid: string
): [
  (
    data: UpdatePricingOfferInput
  ) => Promise<ExecutionResult<EditPricingOfferMutation>>,
  MutationResult<EditPricingOfferMutation>
] {
  const [mutate, result] = useMutation<
    EditPricingOfferMutation,
    EditPricingOfferMutationVariables
  >(EditPricingOffer, {
    update: (cache, result: ExecutionResult<EditPricingOfferMutation>) => {
      const pricingOffer = result.data?.updatePricingOffer;
      cache.writeQuery<AdminPricingOfferQuery, AdminPricingOfferQueryVariables>(
        {
          query: AdminPricingOffer,
          variables: { uid },
          // @ts-ignore TS(2322): Type '{ __typename?: "PricingOffer" | undefined; u... Remove this comment to see the full error message
          data: { pricingOffer }
        }
      );
    }
  });
  const wrapped = React.useCallback(
    async (data: UpdatePricingOfferInput) =>
      mutate({ variables: { uid, data } }),
    [mutate, uid]
  );
  return [wrapped, result];
}

export const DeletePricingOffer = gql`
  mutation DeletePricingOffer($uid: String!) {
    deletePricingOffer(uid: $uid) {
      ...PricingOfferAdminFragment
    }
  }
  ${PricingOfferAdminFragment}
`;

export function useDeletePricingOffer(uid: string) {
  return useMutation<
    DeletePricingOfferMutation,
    DeletePricingOfferMutationVariables
  >(DeletePricingOffer, {
    variables: { uid },
    update: (cache, result: ExecutionResult<DeletePricingOfferMutation>) => {
      const data = cache.readQuery<
        AdminPricingOfferQuery,
        AdminPricingOfferQueryVariables
      >({ query: AdminPricingOffer, variables: { uid } });
      const newPricingOffer = {
        // @ts-ignore TS(2531): Object is possibly 'null'.
        ...data.pricingOffer,
        ...result.data?.deletePricingOffer
      };
      cache.writeQuery<AdminPricingOfferQuery, AdminPricingOfferQueryVariables>(
        {
          query: AdminPricingOffer,
          data: { pricingOffer: newPricingOffer },
          variables: { uid }
        }
      );
    }
  });
}

type PricingOfferPartial = Pick<PricingOffer, "buyerOrgName">;
export function getPricingOfferEnrollmentCopy(
  pricingOffer?: PricingOfferPartial | null
) {
  return pricingOffer && pricingOffer.buyerOrgName
    ? `your ${pricingOffer.buyerOrgName}`
    : `our financial assistance`;
}
